import { useMemo } from 'react';

import Select, { type SelectItem } from '@cartwhl/ui/atoms/Select';
import Text from '@cartwhl/ui/base/Text';

import {
  DEFAULT_SPRING_FALLOFF,
  DEFAULT_SPRING_FALLOFF_CURVE,
  DEFAULT_SPRING_GAIN,
  DEFAULT_SPRING_STIFFNESS,
  DEFAULT_SOLVER_ITERATIONS,
  DEFAULT_SOLVER_MAX_STRETCH,
  SPRING_FALLOFF_CURVES,
  type ChainSolverProperties,
  type SpringFalloffCurve,
  SolverProperties,
  SpringSolverProperties,
  SOLVER_DESCRIPTIONS,
  SOLVER_LABELS,
  SOLVER_TYPES,
  clampSpringUnitValue,
  getDefaultSolver,
  getEffectiveSolverProperties,
  getEffectiveSolverType,
  isSpringSolverProperties,
  isSpringSolverType,
} from '../../../lib/schema/solver';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import {
  INSPECTOR_CONTROL_TEXT_STYLE,
  INSPECTOR_NUMBER_INPUT_CLASS,
  INSPECTOR_SELECT_CLASS,
  INSPECTOR_SELECT_WRAPPER_CLASS,
} from '../constants';
import ResetToDefaultButton from '../../../presentations/ResetToDefaultButton';
import Row from './Row';
import Section from './Section';

interface SolverEditorProps {
  chainName: string;
  solverType: string | null;
  solverProperties: ChainSolverProperties | null;
  onSolverTypeChange?: (chainName: string, solverType: string) => void;
  onSolverPropertiesChange?: (
    chainName: string,
    solverProperties: ChainSolverProperties,
  ) => void;
}

function capitalizeFirstLetter(value: string): string {
  if (!value) return value;
  return `${value[0].toUpperCase()}${value.slice(1)}`;
}

export default function SolverEditor({
  chainName,
  solverType,
  solverProperties,
  onSolverTypeChange,
  onSolverPropertiesChange,
}: SolverEditorProps) {
  const defaultSolverType = getDefaultSolver(chainName).valueOf();
  const effectiveSolverType = getEffectiveSolverType(chainName, solverType);
  const effectiveSolverProperties = getEffectiveSolverProperties(
    effectiveSolverType,
    solverProperties,
  );
  const isSpringSolver = isSpringSolverType(effectiveSolverType);
  const springProperties = isSpringSolverProperties(effectiveSolverProperties)
    ? effectiveSolverProperties
    : new SpringSolverProperties();
  const ikProperties =
    !isSpringSolver && !isSpringSolverProperties(effectiveSolverProperties)
      ? effectiveSolverProperties
      : new SolverProperties();
  const isDefaultType = effectiveSolverType === defaultSolverType;
  const isDefaultIterations = ikProperties.iterations === DEFAULT_SOLVER_ITERATIONS;
  const isDefaultMaxStretch = ikProperties.maxStretch === DEFAULT_SOLVER_MAX_STRETCH;
  const isDefaultReversePv = !ikProperties.reversePv;
  const isDefaultStiffness = springProperties.stiffness === DEFAULT_SPRING_STIFFNESS;
  const isDefaultFalloff = springProperties.falloff === DEFAULT_SPRING_FALLOFF;
  const isDefaultFalloffCurve =
    springProperties.falloffCurve === DEFAULT_SPRING_FALLOFF_CURVE;
  const isDefaultGain = springProperties.gain === DEFAULT_SPRING_GAIN;

  const solverItems = useMemo<SelectItem[]>(() => {
    const items: SelectItem[] = SOLVER_TYPES.map((value) => ({
      value,
      label: SOLVER_LABELS[value],
      description: SOLVER_DESCRIPTIONS[value],
      emphasize: value === defaultSolverType,
    }));
    if (!items.some((item) => item.value.toString() === effectiveSolverType)) {
      items.push({
        value: effectiveSolverType,
        label: effectiveSolverType,
      });
    }
    return items;
  }, [defaultSolverType, effectiveSolverType]);

  const springFalloffCurveItems = useMemo<SelectItem[]>(
    () =>
      SPRING_FALLOFF_CURVES.map((curve) => ({
        value: curve,
        label: capitalizeFirstLetter(curve),
      })),
    [],
  );

  const updateSolverProperties = (nextProperties: ChainSolverProperties) => {
    onSolverPropertiesChange?.(chainName, nextProperties);
  };

  const setIterations = (rawValue: string) => {
    const parsed = Number(rawValue);
    const nextIterations = Number.isFinite(parsed)
      ? Math.max(1, Math.round(parsed))
      : DEFAULT_SOLVER_ITERATIONS;
    updateSolverProperties(
      new SolverProperties(
        nextIterations,
        ikProperties.reversePv,
        ikProperties.maxStretch,
      ),
    );
  };

  const setMaxStretch = (rawValue: string) => {
    const parsed = Number(rawValue);
    const nextMaxStretch = Number.isFinite(parsed)
      ? Math.max(0, parsed)
      : DEFAULT_SOLVER_MAX_STRETCH;
    updateSolverProperties(
      new SolverProperties(
        ikProperties.iterations,
        ikProperties.reversePv,
        nextMaxStretch,
      ),
    );
  };

  const toggleReversePv = () => {
    updateSolverProperties(
      new SolverProperties(
        ikProperties.iterations,
        !ikProperties.reversePv,
        ikProperties.maxStretch,
      ),
    );
  };

  const setSpringStiffness = (rawValue: string) => {
    const parsed = Number(rawValue);
    const nextStiffness = Number.isFinite(parsed)
      ? clampSpringUnitValue(parsed)
      : DEFAULT_SPRING_STIFFNESS;
    updateSolverProperties(
      new SpringSolverProperties(
        nextStiffness,
        springProperties.falloff,
        springProperties.falloffCurve,
        springProperties.gain,
      ),
    );
  };

  const setSpringFalloff = (rawValue: string) => {
    const parsed = Number(rawValue);
    const nextFalloff = Number.isFinite(parsed)
      ? clampSpringUnitValue(parsed)
      : DEFAULT_SPRING_FALLOFF;
    updateSolverProperties(
      new SpringSolverProperties(
        springProperties.stiffness,
        nextFalloff,
        springProperties.falloffCurve,
        springProperties.gain,
      ),
    );
  };

  const setSpringFalloffCurve = (rawValue: string) => {
    const nextFalloffCurve = (SPRING_FALLOFF_CURVES as readonly string[]).includes(
      rawValue,
    )
      ? (rawValue as SpringFalloffCurve)
      : DEFAULT_SPRING_FALLOFF_CURVE;
    updateSolverProperties(
      new SpringSolverProperties(
        springProperties.stiffness,
        springProperties.falloff,
        nextFalloffCurve,
        springProperties.gain,
      ),
    );
  };

  const setSpringGain = (rawValue: string) => {
    const parsed = Number(rawValue);
    const nextGain = Number.isFinite(parsed) ? parsed : DEFAULT_SPRING_GAIN;
    updateSolverProperties(
      new SpringSolverProperties(
        springProperties.stiffness,
        springProperties.falloff,
        springProperties.falloffCurve,
        nextGain,
      ),
    );
  };

  return (
    <Section title="Solver" defaultOpen={false}>
      <div className="space-y-2 pt-0.5">
        <Row label="Chain" value={chainName} />
        <div className="flex items-center justify-between gap-2">
          <Text
            variant="studio/body/medium/xs"
            className={`${PANEL_WRITING_CLASSNAME} ${isDefaultType ? 'font-semibold' : ''}`}
          >
            Type
          </Text>
          <div className="flex shrink-0 items-center gap-1">
            <ResetToDefaultButton
              label="solver type"
              isDefault={isDefaultType}
              onReset={() => onSolverTypeChange?.(chainName, defaultSolverType)}
            />
            <div className={`${INSPECTOR_SELECT_WRAPPER_CLASS} w-[176px]`}>
              <Select
                inModal
                className={`${INSPECTOR_SELECT_CLASS} ${isDefaultType ? 'font-semibold' : ''}`}
                value={effectiveSolverType}
                onValueChange={(value) => onSolverTypeChange?.(chainName, value)}
                items={solverItems}
              />
            </div>
          </div>
        </div>
        {isSpringSolver ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultStiffness ? 'font-semibold' : ''}`}
              >
                Stiffness
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="stiffness"
                  isDefault={isDefaultStiffness}
                  onReset={() => {
                    updateSolverProperties(
                      new SpringSolverProperties(
                        DEFAULT_SPRING_STIFFNESS,
                        springProperties.falloff,
                        springProperties.falloffCurve,
                        springProperties.gain,
                      ),
                    );
                  }}
                />
                <input
                  type="number"
                  className={`${INSPECTOR_NUMBER_INPUT_CLASS} ${isDefaultStiffness ? 'font-semibold' : ''}`}
                  style={INSPECTOR_CONTROL_TEXT_STYLE}
                  min={0}
                  max={1}
                  step={0.05}
                  value={springProperties.stiffness}
                  onChange={(event) => setSpringStiffness(event.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultFalloff ? 'font-semibold' : ''}`}
              >
                Falloff
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="falloff"
                  isDefault={isDefaultFalloff}
                  onReset={() => {
                    updateSolverProperties(
                      new SpringSolverProperties(
                        springProperties.stiffness,
                        DEFAULT_SPRING_FALLOFF,
                        springProperties.falloffCurve,
                        springProperties.gain,
                      ),
                    );
                  }}
                />
                <input
                  type="number"
                  className={`${INSPECTOR_NUMBER_INPUT_CLASS} ${isDefaultFalloff ? 'font-semibold' : ''}`}
                  style={INSPECTOR_CONTROL_TEXT_STYLE}
                  min={0}
                  max={1}
                  step={0.05}
                  value={springProperties.falloff}
                  onChange={(event) => setSpringFalloff(event.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultFalloffCurve ? 'font-semibold' : ''}`}
              >
                Falloff Curve
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="falloff curve"
                  isDefault={isDefaultFalloffCurve}
                  onReset={() => {
                    updateSolverProperties(
                      new SpringSolverProperties(
                        springProperties.stiffness,
                        springProperties.falloff,
                        DEFAULT_SPRING_FALLOFF_CURVE,
                        springProperties.gain,
                      ),
                    );
                  }}
                />
                <div className={`${INSPECTOR_SELECT_WRAPPER_CLASS} w-[176px]`}>
                  <Select
                    inModal
                    className={`${INSPECTOR_SELECT_CLASS} ${isDefaultFalloffCurve ? 'font-semibold' : ''}`}
                    value={springProperties.falloffCurve}
                    onValueChange={setSpringFalloffCurve}
                    items={springFalloffCurveItems}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultGain ? 'font-semibold' : ''}`}
              >
                Gain
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="gain"
                  isDefault={isDefaultGain}
                  onReset={() => {
                    updateSolverProperties(
                      new SpringSolverProperties(
                        springProperties.stiffness,
                        springProperties.falloff,
                        springProperties.falloffCurve,
                        DEFAULT_SPRING_GAIN,
                      ),
                    );
                  }}
                />
                <input
                  type="number"
                  className={`${INSPECTOR_NUMBER_INPUT_CLASS} ${isDefaultGain ? 'font-semibold' : ''}`}
                  style={INSPECTOR_CONTROL_TEXT_STYLE}
                  step={0.05}
                  value={springProperties.gain}
                  onChange={(event) => setSpringGain(event.target.value)}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultIterations ? 'font-semibold' : ''}`}
              >
                Iterations
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="iterations"
                  isDefault={isDefaultIterations}
                  onReset={() => {
                    updateSolverProperties(
                      new SolverProperties(
                        DEFAULT_SOLVER_ITERATIONS,
                        ikProperties.reversePv,
                        ikProperties.maxStretch,
                      ),
                    );
                  }}
                />
                <input
                  type="number"
                  className={`${INSPECTOR_NUMBER_INPUT_CLASS} ${isDefaultIterations ? 'font-semibold' : ''}`}
                  style={INSPECTOR_CONTROL_TEXT_STYLE}
                  min={1}
                  step={1}
                  value={ikProperties.iterations}
                  onChange={(event) => setIterations(event.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultMaxStretch ? 'font-semibold' : ''}`}
              >
                Max Stretch
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="max stretch"
                  isDefault={isDefaultMaxStretch}
                  onReset={() => {
                    updateSolverProperties(
                      new SolverProperties(
                        ikProperties.iterations,
                        ikProperties.reversePv,
                        DEFAULT_SOLVER_MAX_STRETCH,
                      ),
                    );
                  }}
                />
                <input
                  type="number"
                  className={`${INSPECTOR_NUMBER_INPUT_CLASS} ${isDefaultMaxStretch ? 'font-semibold' : ''}`}
                  style={INSPECTOR_CONTROL_TEXT_STYLE}
                  min={0}
                  step={0.05}
                  value={ikProperties.maxStretch}
                  onChange={(event) => setMaxStretch(event.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text
                variant="studio/body/medium/xs"
                className={`${PANEL_WRITING_CLASSNAME} ${isDefaultReversePv ? 'font-semibold' : ''}`}
              >
                Reverse PV
              </Text>
              <div className="flex shrink-0 items-center gap-1">
                <ResetToDefaultButton
                  label="reverse pole vector"
                  isDefault={isDefaultReversePv}
                  onReset={() => {
                    updateSolverProperties(
                      new SolverProperties(
                        ikProperties.iterations,
                        false,
                        ikProperties.maxStretch,
                      ),
                    );
                  }}
                />
                <button
                  type="button"
                  className={`h-3.5 w-7 rounded-full transition-colors ${
                    ikProperties.reversePv
                      ? 'bg-blue-50'
                      : 'bg-gray-30 dark:bg-gray-60'
                  }`}
                  onClick={toggleReversePv}
                  aria-label={`${ikProperties.reversePv ? 'Disable' : 'Enable'} reverse pole vector`}
                  aria-pressed={ikProperties.reversePv}
                >
                  <span
                    className={`block h-2.5 w-2.5 rounded-full bg-white shadow transition-transform ${
                      ikProperties.reversePv
                        ? 'translate-x-3.5'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}
